import axios from 'axios'
import { 
  Product, 
  ShoppingList, 
  User, 
  ApiResponse,
  ProductsData,
  ShoppingListsData,
  CreateProductRequest,
  UpdateProductRequest,
  CreateShoppingListRequest,
  UpdateShoppingListRequest,
  AddToShoppingListRequest,
  GlobalPriceHistoryEntry,
  GetPriceHistoryRequest,
  PhotoAnalysisResult,
  CreateProductFromPhotoRequest
} from '../types'

// Re-export types for components
export type { User, Product }

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api'

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error)
    if (error.response?.status === 401) {
      // Token expired, redirect to login
      localStorage.removeItem('google_token')
      localStorage.removeItem('auth_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Products API
export const productsApi = {
  // Get all products
  getAll: async (): Promise<ApiResponse<ProductsData>> => {
    try {
      const response = await apiClient.get('/products')
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch products'
      }
    }
  },

  // Get all products (alias)
  getProducts: async (): Promise<ApiResponse<ProductsData>> => {
    try {
      const response = await apiClient.get('/products')
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch products'
      }
    }
  },

  // Create a new product
  create: async (product: Product): Promise<ApiResponse<Product>> => {
    try {
      const response = await apiClient.post('/products', { product })
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create product'
      }
    }
  },

  // Create a new product (typed version)
  createProduct: async (request: CreateProductRequest): Promise<ApiResponse<Product>> => {
    try {
      const response = await apiClient.post('/products', request)
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create product'
      }
    }
  },

  // Update a product
  update: async (productName: string, updates: Partial<Product>): Promise<ApiResponse<Product>> => {
    try {
      const response = await apiClient.put(`/products/${encodeURIComponent(productName)}`, {
        updates: updates
      })
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update product'
      }
    }
  },

  // Update a product (typed version)
  updateProduct: async (request: UpdateProductRequest): Promise<ApiResponse<Product>> => {
    try {
      const response = await apiClient.put(`/products/${encodeURIComponent(request.productName)}`, {
        updates: request.updates
      })
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update product'
      }
    }
  },

  // Delete a product
  delete: async (productName: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.delete(`/products/${encodeURIComponent(productName)}`)
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete product'
      }
    }
  },

  // Delete a product (typed version)
  deleteProduct: async (productName: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.delete(`/products/${encodeURIComponent(productName)}`)
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete product'
      }
    }
  },
}

// Shopping Lists API
export const shoppingListsApi = {
  // Get all shopping lists
  getLists: async (): Promise<ApiResponse<ShoppingListsData>> => {
    try {
      const response = await apiClient.get('/shopping-lists')
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch shopping lists'
      }
    }
  },

  // Create a new shopping list
  createList: async (request: CreateShoppingListRequest): Promise<ApiResponse<ShoppingList>> => {
    try {
      const response = await apiClient.post('/shopping-lists', request)
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create shopping list'
      }
    }
  },

  // Update a shopping list
  updateList: async (request: UpdateShoppingListRequest): Promise<ApiResponse<ShoppingList>> => {
    try {
      const response = await apiClient.put(`/shopping-lists/${encodeURIComponent(request.listName)}`, {
        updates: request.updates
      })
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update shopping list'
      }
    }
  },

  // Delete a shopping list
  deleteList: async (listName: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.delete(`/shopping-lists/${encodeURIComponent(listName)}`)
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete shopping list'
      }
    }
  },

  // Add product to shopping list
  addToList: async (request: AddToShoppingListRequest): Promise<ApiResponse<ShoppingList>> => {
    try {
      const response = await apiClient.post(`/shopping-lists/${encodeURIComponent(request.listName)}/products`, {
        productName: request.productName,
        quantity: request.quantity || 1,
        quantityUnit: request.quantityUnit || 'pcs'
      })
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to add item to shopping list'
      }
    }
  },

  // Remove product from shopping list
  removeFromList: async (listName: string, productName: string): Promise<ApiResponse<ShoppingList>> => {
    try {
      const response = await apiClient.delete(`/shopping-lists/${encodeURIComponent(listName)}/products/${encodeURIComponent(productName)}`)
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to remove item from shopping list'
      }
    }
  },
}

// Auth API
export const authApi = {
  // Login with Google token
  login: async (googleToken: string): Promise<ApiResponse<{ user: User; token: string }>> => {
    try {
      const response = await apiClient.post('/auth/login', {
        googleToken: googleToken
      })
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Login failed'
      }
    }
  },

  // Refresh JWT token
  refresh: async (): Promise<ApiResponse<{ token: string }>> => {
    try {
      const response = await apiClient.post('/auth/refresh')
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Token refresh failed'
      }
    }
  },

  // Logout
  logout: async (): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.post('/auth/logout')
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Logout failed'
      }
    }
  },

  // Validate Google token with backend (legacy compatibility)
  validateToken: async (googleToken: string): Promise<ApiResponse<{ user: User }>> => {
    const loginResponse = await authApi.login(googleToken)
    if (loginResponse.success && loginResponse.data) {
      return {
        success: true,
        data: { user: loginResponse.data.user }
      }
    }
    return {
      success: false,
      error: loginResponse.error || 'Token validation failed'
    }
  },
}

// Health check
export const healthApi = {
  check: async (): Promise<ApiResponse<{ status: string; timestamp: string }>> => {
    try {
      const response = await apiClient.get('/health')
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Health check failed'
      }
    }
  },
}

// Price History API
export const priceHistoryApi = {
  async getPriceHistory(filters: GetPriceHistoryRequest = {}): Promise<ApiResponse<GlobalPriceHistoryEntry[]>> {
    try {
      const params = new URLSearchParams()
      if (filters.productName) params.append('productName', filters.productName)
      if (filters.store) params.append('store', filters.store)
      if (filters.startDate) params.append('startDate', filters.startDate)
      if (filters.endDate) params.append('endDate', filters.endDate)
      if (filters.limit) params.append('limit', filters.limit.toString())

      const response = await apiClient.get(`/price-history?${params.toString()}`)
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get price history'
      }
    }
  },
}

// AI Photo Analysis API
export const aiApi = {
  async analyzePhoto(request: CreateProductFromPhotoRequest): Promise<ApiResponse<PhotoAnalysisResult>> {
    try {
      const response = await apiClient.post('/analyze-photo', request)
      return response.data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to analyze photo'
      }
    }
  },
}

export default {
  products: productsApi,
  shoppingLists: shoppingListsApi,
  auth: authApi,
  health: healthApi,
  priceHistory: priceHistoryApi,
  ai: aiApi,
} 